import {
  DIAGONAL,
  KNIGHT,
  ORTHOGONAL,
  enemyBackRow,
  forwardComponent,
  homeRow,
  onBoard,
  squareIndex,
  type Occupancy,
} from './board';
import type { Dir, Face, Pawn, RecordedMove, Square } from './types';

/**
 * Movement rules (spec §8). Pure functions over an occupancy grid; they never touch MatchState.
 */

/** Occupancy marker of a Pillar (Long Night Omen): it blocks, can be hopped, is never captured. */
export const PILLAR = '#';

/** What a Long Night pawn may do beyond §8 (Carvings and statuses, LEDGER.MD). */
export interface PawnFlags {
  /** Slides pass through friendly pawns (Hollow Base). */
  readonly passFriends?: boolean;
  /** Slides pass through every piece but Pillars, capturing nothing on the way (Phased). */
  readonly phased?: boolean;
  /** Slides go through enemy pawns and capture each one (Comet). */
  readonly comet?: boolean;
  /** Red and Blue slides may go one square shorter or longer (Hasted). */
  readonly hasted?: boolean;
  /** Red and Blue slides go exactly one square (Heavy). */
  readonly heavy?: boolean;
  /** Yellow hops may also go straight (Kite). */
  readonly kite?: boolean;
  /** A Green card moves it up to N knight jumps (Knight's Gait). */
  readonly knightGait?: boolean;
  /** Uncrowned, it may move only backward this turn (Reverse). */
  readonly reversed?: boolean;
}

/** Long Night changes to movement; absent for the rules 1.0 game. */
export interface MoveMods {
  /** Whether `target` cannot be captured by `mover` (replaces the home-row check). */
  readonly immune: (target: Pawn, mover: Pawn) => boolean;
  /** Whether a Yellow hop may jump `target` (Iron Shell). */
  readonly jumpable: (target: Pawn) => boolean;
  readonly flags: (pawn: Pawn) => PawnFlags;
}

export interface BoardCtx {
  /** Pawn id per square index, PILLAR, or null. */
  readonly occ: Occupancy;
  readonly pawns: Readonly<Record<string, Pawn>>;
  readonly immunity: boolean;
  readonly mods?: MoveMods;
}

/** A legal move for one card (one link). */
export interface MoveOption {
  /** R/B/G: the single end square. Y: every hop's landing square in order. */
  readonly landings: readonly Square[];
  /** Captured pawn ids, in order. */
  readonly captures: readonly string[];
  /** For each landing, whether a capture happened on that move or hop. */
  readonly captureAt: readonly boolean[];
  readonly move: RecordedMove;
}

export type MoveFailure =
  | 'OFF_BOARD'
  | 'BACKWARD'
  | 'BLOCKED'
  | 'OWN_PIECE'
  | 'IMMUNE'
  | 'NOTHING_TO_JUMP'
  | 'LANDING_OCCUPIED'
  | 'JUMPED_TWICE'
  | 'WRONG_SHAPE';

export type MoveCheck =
  | { readonly ok: true; readonly option: MoveOption }
  | { readonly ok: false; readonly reason: MoveFailure };

const fail = (reason: MoveFailure): MoveCheck => ({ ok: false, reason });

const pawnAt = (ctx: BoardCtx, occ: Occupancy, x: number, y: number): Pawn | null => {
  const id = occ[y * 6 + x];
  return id && id !== PILLAR ? (ctx.pawns[id] ?? null) : null;
};

const isImmuneAt = (ctx: BoardCtx, pawn: Pawn, y: number): boolean =>
  ctx.immunity && y === homeRow(pawn.side);

/** Whether `target` cannot be captured by `mover` (§8.7, and the Long Night's changes). */
const immuneTo = (ctx: BoardCtx, target: Pawn, mover: Pawn, y: number): boolean =>
  ctx.mods ? ctx.mods.immune(target, mover) : isImmuneAt(ctx, target, y);

const NO_FLAGS: PawnFlags = {};
const flagsOf = (ctx: BoardCtx, pawn: Pawn): PawnFlags => ctx.mods?.flags(pawn) ?? NO_FLAGS;

/**
 * True when an uncrowned pawn would move backward with this dy (§8.1 direction rule). Long Night:
 * a Reversed side's uncrowned pawns may not move forward instead.
 */
const violatesDirection = (ctx: BoardCtx, pawn: Pawn, dy: number): boolean => {
  if (pawn.crowned) return false;
  const f = forwardComponent(pawn.side, dy);
  return flagsOf(ctx, pawn).reversed ? f > 0 : f < 0;
};

/** Checks a Red or Blue slide of exactly `dist` squares in unit direction (ux, uy). */
export function checkSlide(
  ctx: BoardCtx,
  pawn: Pawn,
  ux: number,
  uy: number,
  dist: number,
): MoveCheck {
  const from = pawn.square;
  if (!from) return fail('OFF_BOARD');
  if (violatesDirection(ctx, pawn, uy)) return fail('BACKWARD');
  const tx = from.x + ux * dist;
  const ty = from.y + uy * dist;
  if (!onBoard(tx, ty)) return fail('OFF_BOARD');
  const flags = flagsOf(ctx, pawn);
  const passed: string[] = [];
  for (let i = 1; i < dist; i++) {
    const id = ctx.occ[(from.y + uy * i) * 6 + from.x + ux * i];
    if (!id) continue;
    if (id === PILLAR) return fail('BLOCKED');
    if (flags.phased) continue;
    const p = ctx.pawns[id];
    if (p && flags.passFriends && p.side === pawn.side) continue;
    if (p && flags.comet && p.side !== pawn.side && !immuneTo(ctx, p, pawn, from.y + uy * i)) {
      passed.push(id);
      continue;
    }
    return fail('BLOCKED');
  }
  if (ctx.occ[ty * 6 + tx] === PILLAR) return fail('BLOCKED');
  const target = pawnAt(ctx, ctx.occ, tx, ty);
  const to = { x: tx, y: ty };
  const move = { type: 'slide' as const, dx: ux * dist, dy: uy * dist };
  if (target) {
    if (target.side === pawn.side) return fail('OWN_PIECE');
    if (immuneTo(ctx, target, pawn, ty)) return fail('IMMUNE');
    return {
      ok: true,
      option: { landings: [to], captures: [...passed, target.id], captureAt: [true], move },
    };
  }
  return {
    ok: true,
    option: { landings: [to], captures: passed, captureAt: [passed.length > 0], move },
  };
}

/** Checks one Green knight jump (§8.4). */
export function checkKnight(ctx: BoardCtx, pawn: Pawn, dx: number, dy: number): MoveCheck {
  const from = pawn.square;
  if (!from) return fail('OFF_BOARD');
  const shapeOk =
    (Math.abs(dx) === 1 && Math.abs(dy) === 2) || (Math.abs(dx) === 2 && Math.abs(dy) === 1);
  if (!shapeOk) return fail('WRONG_SHAPE');
  if (violatesDirection(ctx, pawn, dy)) return fail('BACKWARD');
  const tx = from.x + dx;
  const ty = from.y + dy;
  if (!onBoard(tx, ty)) return fail('OFF_BOARD');
  if (ctx.occ[ty * 6 + tx] === PILLAR) return fail('BLOCKED');
  const target = pawnAt(ctx, ctx.occ, tx, ty);
  const to = { x: tx, y: ty };
  if (target) {
    if (target.side === pawn.side) return fail('OWN_PIECE');
    if (immuneTo(ctx, target, pawn, ty)) return fail('IMMUNE');
    return {
      ok: true,
      option: {
        landings: [to],
        captures: [target.id],
        captureAt: [true],
        move: { type: 'knight', dx, dy },
      },
    };
  }
  return {
    ok: true,
    option: { landings: [to], captures: [], captureAt: [false], move: { type: 'knight', dx, dy } },
  };
}

/**
 * Checks a sequence of Yellow hops (§8.5). The pawn's crowned status at the start of the card
 * applies to every hop; jumped enemies are captured immediately; no piece is jumped twice.
 */
export function checkHops(ctx: BoardCtx, pawn: Pawn, dirs: readonly Dir[]): MoveCheck {
  const from = pawn.square;
  if (!from) return fail('OFF_BOARD');
  if (dirs.length === 0) return fail('WRONG_SHAPE');
  const kite = flagsOf(ctx, pawn).kite === true;
  const occ = ctx.occ.slice();
  occ[squareIndex(from)] = null;
  const jumped = new Set<string>();
  const landings: Square[] = [];
  const captures: string[] = [];
  const captureAt: boolean[] = [];
  let x = from.x;
  let y = from.y;
  for (const d of dirs) {
    const diagonal = Math.abs(d.dx) === 1 && Math.abs(d.dy) === 1;
    const straight = Math.abs(d.dx) + Math.abs(d.dy) === 1;
    if (!diagonal && !(kite && straight)) return fail('WRONG_SHAPE');
    if (violatesDirection(ctx, pawn, d.dy)) return fail('BACKWARD');
    const mx = x + d.dx;
    const my = y + d.dy;
    const lx = x + 2 * d.dx;
    const ly = y + 2 * d.dy;
    if (!onBoard(lx, ly)) return fail('OFF_BOARD');
    const midId = occ[my * 6 + mx];
    if (!midId) return fail('NOTHING_TO_JUMP');
    const key = midId === PILLAR ? `${PILLAR}${mx},${my}` : midId;
    if (jumped.has(key)) return fail('JUMPED_TWICE');
    if (occ[ly * 6 + lx]) return fail('LANDING_OCCUPIED');
    jumped.add(key);
    let captured = false;
    if (midId !== PILLAR) {
      const mid = ctx.pawns[midId] as Pawn;
      if (ctx.mods && !ctx.mods.jumpable(mid)) return fail('BLOCKED');
      if (mid.side !== pawn.side) {
        if (immuneTo(ctx, mid, pawn, my)) return fail('IMMUNE');
        occ[my * 6 + mx] = null;
        captures.push(midId);
        captured = true;
      }
    }
    x = lx;
    y = ly;
    landings.push({ x, y });
    captureAt.push(captured);
  }
  return {
    ok: true,
    option: {
      landings,
      captures,
      captureAt,
      move: { type: 'hops', dirs: dirs.map((d) => ({ dx: d.dx, dy: d.dy })) },
    },
  };
}

/**
 * Checks a sequence of knight jumps with one Green card (Knight's Gait, Long Night). Each jump
 * follows §8.4; a captured pawn leaves the board at once and the pawn jumps on.
 */
export function checkKnights(
  ctx: BoardCtx,
  pawn: Pawn,
  jumps: readonly { dx: number; dy: number }[],
): MoveCheck {
  const from = pawn.square;
  if (!from) return fail('OFF_BOARD');
  if (jumps.length === 0) return fail('WRONG_SHAPE');
  const occ = ctx.occ.slice();
  occ[squareIndex(from)] = null;
  const landings: Square[] = [];
  const captures: string[] = [];
  const captureAt: boolean[] = [];
  let x = from.x;
  let y = from.y;
  for (const j of jumps) {
    const shapeOk =
      (Math.abs(j.dx) === 1 && Math.abs(j.dy) === 2) ||
      (Math.abs(j.dx) === 2 && Math.abs(j.dy) === 1);
    if (!shapeOk) return fail('WRONG_SHAPE');
    if (violatesDirection(ctx, pawn, j.dy)) return fail('BACKWARD');
    const tx = x + j.dx;
    const ty = y + j.dy;
    if (!onBoard(tx, ty)) return fail('OFF_BOARD');
    const id = occ[ty * 6 + tx];
    if (id === PILLAR) return fail('BLOCKED');
    if (sameXY(from, tx, ty)) return fail('LANDING_OCCUPIED');
    let captured = false;
    if (id) {
      const target = ctx.pawns[id] as Pawn;
      if (target.side === pawn.side) return fail('OWN_PIECE');
      if (immuneTo(ctx, target, pawn, ty)) return fail('IMMUNE');
      occ[ty * 6 + tx] = null;
      captures.push(id);
      captured = true;
    }
    x = tx;
    y = ty;
    landings.push({ x, y });
    captureAt.push(captured);
  }
  return {
    ok: true,
    option: {
      landings,
      captures,
      captureAt,
      move: { type: 'knights', jumps: jumps.map((j) => ({ dx: j.dx, dy: j.dy })) },
    },
  };
}

const sameXY = (sq: Square, x: number, y: number): boolean => sq.x === x && sq.y === y;

/** Distances a Red or Blue card of number n can slide this pawn (Hasted, Heavy). */
function slideDistances(flags: PawnFlags, n: number): number[] {
  if (flags.heavy) return [1];
  if (flags.hasted) return [n - 1, n, n + 1].filter((d) => d >= 1);
  return [n];
}

const HOP_DIRS_KITE: readonly Dir[] = [
  ...DIAGONAL,
  { dx: 0, dy: 1 },
  { dx: 0, dy: -1 },
  { dx: 1, dy: 0 },
  { dx: -1, dy: 0 },
];

/** Checks a recorded move exactly as recorded (Epitaph replay, §10.4). */
export function checkRecordedMove(
  ctx: BoardCtx,
  pawn: Pawn,
  face: Face,
  move: RecordedMove,
): MoveCheck {
  switch (face.color) {
    case 'R':
    case 'B': {
      if (move.type !== 'slide') return fail('WRONG_SHAPE');
      const dist = Math.max(Math.abs(move.dx), Math.abs(move.dy));
      // A Long Night route may have been slid a square shorter or longer (Hasted, Heavy).
      if (ctx.mods ? dist < 1 || dist > face.number + 1 : dist !== face.number)
        return fail('WRONG_SHAPE');
      const ux = Math.sign(move.dx);
      const uy = Math.sign(move.dy);
      const orthogonal = (ux === 0) !== (uy === 0);
      const diagonal = Math.abs(move.dx) === Math.abs(move.dy) && ux !== 0;
      if (face.color === 'R' ? !orthogonal : !diagonal) return fail('WRONG_SHAPE');
      return checkSlide(ctx, pawn, ux, uy, dist);
    }
    case 'G':
      if (move.type === 'knights') {
        if (!ctx.mods || move.jumps.length < 1 || move.jumps.length > face.number)
          return fail('WRONG_SHAPE');
        return checkKnights(ctx, pawn, move.jumps);
      }
      if (move.type !== 'knight') return fail('WRONG_SHAPE');
      return checkKnight(ctx, pawn, move.dx, move.dy);
    case 'Y':
      if (move.type !== 'hops' || move.dirs.length < 1 || move.dirs.length > face.number)
        return fail('WRONG_SHAPE');
      if (!ctx.mods && move.dirs.some((d) => d.dx === 0 || d.dy === 0)) return fail('WRONG_SHAPE');
      return checkRecordedHops(ctx, pawn, move.dirs);
  }
}

/** A recorded route keeps its straight hops even when the replaying pawn has no Kite. */
function checkRecordedHops(ctx: BoardCtx, pawn: Pawn, dirs: readonly Dir[]): MoveCheck {
  if (!ctx.mods || dirs.every((d) => d.dx !== 0 && d.dy !== 0)) return checkHops(ctx, pawn, dirs);
  const mods = ctx.mods;
  const kiteCtx: BoardCtx = {
    ...ctx,
    mods: { ...mods, flags: (p) => ({ ...mods.flags(p), kite: true }) },
  };
  return checkHops(kiteCtx, pawn, dirs);
}

/** Every legal move for a pawn and face (§8). */
export function generateMoves(ctx: BoardCtx, pawn: Pawn, face: Face): MoveOption[] {
  const out: MoveOption[] = [];
  if (pawn.status !== 'onBoard' || !pawn.square) return out;
  const flags = flagsOf(ctx, pawn);
  switch (face.color) {
    case 'R':
    case 'B':
      for (const d of face.color === 'R' ? ORTHOGONAL : DIAGONAL)
        for (const dist of slideDistances(flags, face.number)) {
          const r = checkSlide(ctx, pawn, d.dx, d.dy, dist);
          if (r.ok) out.push(r.option);
        }
      break;
    case 'G':
      if (flags.knightGait) {
        const walk = (prefix: { dx: number; dy: number }[]): void => {
          for (const d of KNIGHT) {
            const jumps = [...prefix, d];
            const r = checkKnights(ctx, pawn, jumps);
            if (!r.ok) continue;
            out.push(r.option);
            if (jumps.length < face.number) walk(jumps);
          }
        };
        walk([]);
        break;
      }
      for (const d of KNIGHT) {
        const r = checkKnight(ctx, pawn, d.dx, d.dy);
        if (r.ok) out.push(r.option);
      }
      break;
    case 'Y': {
      const dirs = flags.kite ? HOP_DIRS_KITE : DIAGONAL;
      const walk = (prefix: Dir[]): void => {
        for (const d of dirs) {
          const path = [...prefix, d];
          const r = checkHops(ctx, pawn, path);
          if (!r.ok) continue;
          out.push(r.option);
          if (path.length < face.number) walk(path);
        }
      };
      walk([]);
      break;
    }
  }
  return out;
}

/** True when the pawn has at least one legal move for the face (cheaper than generating all). */
export function hasMove(ctx: BoardCtx, pawn: Pawn, face: Face): boolean {
  if (pawn.status !== 'onBoard' || !pawn.square) return false;
  const flags = flagsOf(ctx, pawn);
  switch (face.color) {
    case 'R':
    case 'B': {
      const dirs = face.color === 'R' ? ORTHOGONAL : DIAGONAL;
      const dists = slideDistances(flags, face.number);
      return dirs.some((d) => dists.some((n) => checkSlide(ctx, pawn, d.dx, d.dy, n).ok));
    }
    case 'G':
      // A legal series of jumps starts with a legal single jump.
      return KNIGHT.some((d) =>
        flags.knightGait ? checkKnights(ctx, pawn, [d]).ok : checkKnight(ctx, pawn, d.dx, d.dy).ok,
      );
    case 'Y':
      // Any legal multi-hop sequence starts with a legal single hop.
      return (flags.kite ? HOP_DIRS_KITE : DIAGONAL).some((d) => checkHops(ctx, pawn, [d]).ok);
  }
}

/** Would this pawn crown on arriving at `to`? */
export const crownsAt = (pawn: Pawn, to: Square): boolean =>
  !pawn.crowned && to.y === enemyBackRow(pawn.side);

/** Occupancy with one pawn removed (Wild collateral, §9.4). */
export function withoutPawn(ctx: BoardCtx, pawnId: string): BoardCtx {
  const p = ctx.pawns[pawnId];
  if (!p || p.status !== 'onBoard' || !p.square) return ctx;
  const occ = ctx.occ.slice();
  occ[squareIndex(p.square)] = null;
  return { ...ctx, occ };
}
